import { Injectable } from "@angular/core";
import { CriticalPointTemplate } from "src/app/classes/model/practice-path";
import { BackendService } from "src/app/services/common/backend.service";
import { HttpErrorHandlerService } from "src/app/services/common/http-error-handler.service";

const BASE_API_URL = "/api/admin/critical-point-templates";

@Injectable({
  providedIn: "root",
})
export class CriticalPointTemplateService {
  constructor(
    private backendService: BackendService,
    private httpErrorHandler: HttpErrorHandlerService
  ) {}

  async createTemplate(
    payload: CreateCriticalPointTemplatePayload
  ): Promise<CriticalPointTemplate | undefined> {
    try {
      const response = await this.backendService.callApi<
        CreateCriticalPointTemplatePayload,
        CriticalPointTemplateDto
      >(BASE_API_URL, "POST", payload);

      return this.mapTemplate(response);
    } catch (error) {
      this.httpErrorHandler.handleError(
        error,
        "Kritikus pont sablon létrehozása nem sikerült."
      );
      return undefined;
    }
  }

  async updateTemplate(
    templateId: string,
    changes: UpdateCriticalPointTemplatePayload
  ): Promise<CriticalPointTemplate | undefined> {
    if (!this.hasTemplateChanges(changes)) {
      return undefined;
    }

    try {
      const response = await this.backendService.callApi<
        UpdateCriticalPointTemplatePayload,
        CriticalPointTemplateDto
      >(`${BASE_API_URL}/${templateId}`, "PATCH", changes);

      return this.mapTemplate(response);
    } catch (error) {
      this.httpErrorHandler.handleError(
        error,
        "Kritikus pont sablon frissítése nem sikerült."
      );
      return undefined;
    }
  }

  async incrementUsageCount(
    templateId: string,
    increment: number = 1
  ): Promise<CriticalPointTemplate | undefined> {
    const normalizedIncrement = Math.max(1, Math.floor(increment));
    const body: IncrementUsageCountPayload = { increment: normalizedIncrement };

    try {
      const response = await this.backendService.callApi<
        IncrementUsageCountPayload,
        CriticalPointTemplateDto
      >(`${BASE_API_URL}/${templateId}/usage-count`, "PATCH", body);

      return this.mapTemplate(response);
    } catch (error) {
      this.httpErrorHandler.handleError(
        error,
        "Kritikus pont sablon használati számlálójának frissítése nem sikerült."
      );
      return undefined;
    }
  }

  async searchTemplates(
    params: CriticalPointTemplateSearchParams = {}
  ): Promise<CriticalPointTemplateSearchResponse | undefined> {
    const apiUrl = this.appendQueryParams(BASE_API_URL, params);

    try {
      const response = await this.backendService.callApi<
        undefined,
        CriticalPointTemplateSearchResponseDto
      >(apiUrl, "GET");

      return this.mapSearchResponse(response);
    } catch (error) {
      this.httpErrorHandler.handleError(
        error,
        "Kritikus pont sablonok lekérdezése nem sikerült."
      );
      return undefined;
    }
  }

  async deleteTemplate(templateId: string): Promise<boolean> {
    try {
      await this.backendService.callApi(`${BASE_API_URL}/${templateId}`, "DELETE");
      return true;
    } catch (error) {
      this.httpErrorHandler.handleError(
        error,
        "Kritikus pont sablon törlése nem sikerült."
      );
      return false;
    }
  }

  private appendQueryParams(
    baseUrl: string,
    params: CriticalPointTemplateSearchParams
  ): string {
    const urlSearchParams = new URLSearchParams();

    if (params.page !== undefined) {
      urlSearchParams.set("page", params.page.toString());
    }
    if (params.pageSize !== undefined) {
      urlSearchParams.set("pageSize", params.pageSize.toString());
    }
    if (params.sortBy !== undefined) {
      urlSearchParams.set("sortBy", params.sortBy);
    }
    if (params.sortOrder !== undefined) {
      urlSearchParams.set("sortOrder", params.sortOrder);
    }
    if (params.filterLogic !== undefined) {
      urlSearchParams.set("filterLogic", params.filterLogic);
    }
    if (params.titleContains) {
      urlSearchParams.set("titleContains", params.titleContains);
    }
    if (params.descriptionContains) {
      urlSearchParams.set("descriptionContains", params.descriptionContains);
    }

    const queryString = urlSearchParams.toString();

    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  private mapSearchResponse(
    response: CriticalPointTemplateSearchResponseDto
  ): CriticalPointTemplateSearchResponse {
    return {
      ...response,
      items: response.items.map((item) => this.mapTemplate(item)),
    };
  }

  private mapTemplate(dto: CriticalPointTemplateDto): CriticalPointTemplate {
    return {
      ...dto,
      createdAt: new Date(dto.createdAt),
      updatedAt: new Date(dto.updatedAt),
    };
  }

  private hasTemplateChanges(
    changes: UpdateCriticalPointTemplatePayload | undefined
  ): changes is UpdateCriticalPointTemplatePayload {
    if (!changes) {
      return false;
    }

    return (
      (changes.title !== undefined && changes.title.trim().length > 0) ||
      (changes.description !== undefined &&
        changes.description.trim().length > 0) ||
      changes.isGlobal !== undefined
    );
  }
}

export type CreateCriticalPointTemplatePayload = {
  title: string;
  description: string;
  isGlobal: boolean;
};

export type UpdateCriticalPointTemplatePayload =
  Partial<CreateCriticalPointTemplatePayload>;

type CriticalPointTemplateDto = Omit<
  CriticalPointTemplate,
  "createdAt" | "updatedAt"
> & {
  createdAt: string;
  updatedAt: string;
};

type IncrementUsageCountPayload = {
  increment: number;
};

export type CriticalPointTemplateSearchParams = {
  page?: number;
  pageSize?: number;
  sortBy?: "createdAt" | "usageCount";
  sortOrder?: "asc" | "desc";
  titleContains?: string;
  descriptionContains?: string;
  filterLogic?: "AND" | "OR";
};

type CriticalPointTemplateSearchResponseDto = {
  items: CriticalPointTemplateDto[];
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;
};

export type CriticalPointTemplateSearchResponse = {
  items: CriticalPointTemplate[];
  totalCount: number;
  totalPages: number;
  page: number;
  pageSize: number;
};

